/**
 * Vehicle plan-view glass panes, generated from docs/ui/vehicle-glass-plan.svg.
 *
 * The artwork carries NO id attributes, so nothing in it says which path is which pane.
 * Each is identified by bounding-box centroid, asserted to land within 3% of an expected
 * position, with the two leftovers taken as the wing mirrors. Generated rather than
 * hand-transcribed so a typo cannot silently mislabel a window.
 *
 * The roof arrives as a rounded <rect>; it is converted to an equivalent path here so the
 * data model stays uniform, with the conversion asserted against the rect it replaced.
 *
 * There is no group transform in this artwork and no body silhouette: the panes describe
 * the car on their own, so nothing is invented to sit behind them.
 *
 * Slots are PHYSICAL (left/right); the API enums are ROLE-based (driver/passenger).
 * Mapping between them is what the handedness gate exists to decide.
 */

export const PLAN_VIEWBOX = '0.0 0.0 70.004 125.47';

export type PaneSlot =
  | 'windscreen'
  | 'leftFront'
  | 'rightFront'
  | 'leftRear'
  | 'rightRear'
  | 'leftQuarter'
  | 'rightQuarter'
  | 'sunroof'
  | 'rearScreen';

export interface Pane {
  slot: PaneSlot;
  d: string;
  /** Badge position as a percentage of the rendered box. */
  left: number;
  top: number;
  /** Windscreen, roof and rear screen are the same window in either market. */
  handednessIndependent: boolean;
}

export const PANES: Pane[] = [
  {
    slot: 'windscreen',
    left: 50.1,
    top: 17.4,
    handednessIndependent: true,
    d: 'm5.8187 10.653 1.0742-1.7904 1.9694-1.7904 2.417-1.7904 6.2663-2.9541 6.9825-1.6113 7.072-0.71615h5.5502l7.251 0.62663 5.6397 1.4323 5.3711 1.9694 4.0283 2.3275 1.9694 1.4323 2.238 2.0589 0.71615 0.98471-7.251 32.943-2.6856-1.6113-1.7154-0.44107-2.437-0.66464-3.3865-0.3165-4.1144-0.47474-12.438-0.15825-5.4437 0.41145-2.6586 0.18989-3.6081 0.5697-2.6586 0.72794-1.6774 0.53804-1.4875 0.88619z',
  },
  {
    slot: 'leftFront',
    left: 8.3,
    top: 39.8,
    handednessIndependent: false,
    d: 'm11.458 63.2-11.011-3.9388-0.26856-22.469 8.2357-0.0895 1.0742 4.0283 0.71615 4.2969 0.62663 4.9235 0.35807 5.2816 0.17904 4.834z',
  },
  {
    slot: 'rightFront',
    left: 91.4,
    top: 39.5,
    handednessIndependent: false,
    d: 'm58.098 63.021 11.011-3.8493 0.71615-22.559-8.4148-0.62663-1.1638 4.9235-0.62662 4.2969-0.53712 4.9235-0.35807 3.8493-0.35808 4.2074z',
  },
  {
    slot: 'leftRear',
    left: 9.4,
    top: 63.4,
    handednessIndependent: false,
    d: 'm2.3275 92.831c0.35807 0.0895 8.7728 1.7904 8.7728 1.7904l0.71615-6.4453v-4.2969l0.08952-5.7292-0.35807-5.9082 0.08952-4.5654-10.295-3.2227-0.08952 6.4453 0.26856 5.8187 0.26856 7.7881 0.17904 5.013z',
  },
  {
    slot: 'rightRear',
    left: 90.5,
    top: 63.3,
    handednessIndependent: false,
    d: 'm58.545 66.602 9.9366-2.7751-0.98471 29.094-8.6833 2.1484-0.62663-6.0873v-11.727l0.0895-5.2816z',
  },
  {
    slot: 'leftQuarter',
    left: 9.2,
    top: 81.0,
    handednessIndependent: false,
    d: 'm10.742 96.68-8.5938-1.0742 0.53711 6.8034 0.62663 2.6856 0.71615 1.7008 1.8799 0.80567 1.7009-0.9847 1.4323-2.5065 0.80567-2.417 0.80567-2.8646z',
  },
  {
    slot: 'rightQuarter',
    left: 90.5,
    top: 81.0,
    handednessIndependent: false,
    d: 'm59.172 97.217 8.3252-1.6113-0.53711 5.1921-0.35807 2.6856-0.71615 2.1484-0.71615 1.2533-1.1637 0.89519-1.5218-0.89519-1.4323-2.238-1.3428-3.7598z',
  },
  {
    slot: 'sunroof',
    left: 50.8,
    top: 45.0,
    handednessIndependent: true,
    d: 'M23.4617,49.255 H47.6533 A3.2227,3.2227 0 0 1 50.876000000000005,52.477700000000006 V60.3383 A3.2227,3.2227 0 0 1 47.6533,63.561 H23.4617 A3.2227,3.2227 0 0 1 20.239,60.3383 V52.477700000000006 A3.2227,3.2227 0 0 1 23.4617,49.255 Z',
  },
  {
    slot: 'rearScreen',
    left: 49.7,
    top: 93.0,
    handednessIndependent: true,
    d: 'm16.093 107.96 1.5508 0.3798 1.9306 0.28484 3.6714 0.41145 3.9562 0.22154 4.0195 0.15825 5.6969-0.0633 4.3676-0.1266 5.0006-0.095 2.9434-0.44309 2.532-0.41144 1.7091-0.47475 0.37979 0.1899 4.9057 8.4188 0.2532 1.9939-0.0633 1.4875-0.82289 1.2976-1.6141 1.2976-1.8673 1.0444-3.7346 1.0128-3.5448 0.53804-3.2916 0.1899-4.7474 0.1899h-4.9373l-6.6148-0.0633-4.1461-0.34815-5.0639-0.56969-3.2599-0.60134-2.1522-1.2027-2.1205-2.2788-0.41144-1.3609v-1.7724l0.79124-1.5508z',
  },
];

/** Non-interactive; drawn so the plan reads as a vehicle. */
export const MIRROR_PATHS: string[] = [
  'm0.26856 34.196 7.5196-0.26856-5.1026-19.336-2.6856 6.3558z',
  'm62.394 32.406 7.251 0.26855 0.35808-13.159-2.7751-5.3711z',
];
